import { Button } from "components/Button/Button";
import { ActionDropdownButton } from "components/DropdownButton/ActionDropdownButton";
import ElementLoader from "components/ElementLoader";
import { IconLike } from "components/Icon/Icon";

interface SaveSplitButtonProps {
    canCommit: boolean;
    icon?: IconLike;
    isDisabled: boolean;
    // Spins + self-disables the split Save button while a save is running.
    isLoading: boolean;
    // Additionally disables (without spinning) while any other form action runs.
    isBusy?: boolean;
    saveFormWithCommit: (canCommit: boolean) => void;
    overlayClassName?: string;
}

export interface SaveSplitAction {
    // Label shown on the control.
    label: string;
    // The argument passed to saveFormWithCommit when this action is triggered:
    // true = save AND commit, false = save without committing.
    commit: boolean;
}

// Maps the autocommit-derived `canCommit` flag to the split button's primary
// (single-click) and secondary (dropdown) actions. `canCommit` is the
// GIT_AUTOCOMMIT_ENABLED-derived default (see useCanCommitOnSave): when the
// workspace has autocommit ON the primary single-click action commits and a
// plain (non-committing) Save lives in the dropdown; when OFF the primary action
// is a plain Save and "Save and commit" moves into the dropdown. Either way the
// user can always reach both a committing and a non-committing save — the
// workspace setting only decides which one is the single-click default.
export const getSaveSplitButtonActions = (
    canCommit: boolean
): { primary: SaveSplitAction; secondary: SaveSplitAction } => {
    const toAction = (commit: boolean): SaveSplitAction => ({
        commit,
        label: commit ? "Save and commit" : "Save",
    });
    return { primary: toAction(canCommit), secondary: toAction(!canCommit) };
};

export const SaveSplitButton = ({
    canCommit,
    icon,
    isDisabled,
    isLoading,
    isBusy,
    saveFormWithCommit,
    overlayClassName,
}: SaveSplitButtonProps) => {
    const { primary, secondary } = getSaveSplitButtonActions(canCommit);
    const parentProps = { action: () => saveFormWithCommit(primary.commit), label: primary.label };
    const childProps = { action: () => saveFormWithCommit(secondary.commit), label: secondary.label };

    // Replace the icon with the spinner while saving, keeping the label — the
    // same treatment as the Commit button. (see ui-design-principle-buttons)
    const loadingIcon = isLoading
        ? () => (
              <div className="actionButtonLoader">
                  <ElementLoader />
              </div>
          )
        : null;

    return (
        <ActionDropdownButton
            icon={loadingIcon ?? icon}
            isLoading={isLoading}
            isDisabled={isDisabled || isLoading || isBusy}
            parentAction={parentProps.action}
            label={parentProps.label}
            overlayClassName={overlayClassName}
            tooltipOverlay="Ctrl+S"
        >
            <Button
                icon={icon}
                variation="clear"
                className="wide"
                disabled={isDisabled || isLoading || isBusy}
                onClick={childProps.action}
                label={childProps.label}
            />
        </ActionDropdownButton>
    );
};
